import { useForm, SubmitHandler } from "react-hook-form";
import { Form } from "@/components/shared/forms/Form";
import { useRouteContext } from "@tanstack/react-router";
import { SCLR, SCLR_TEMPLATE, LeaDataModel, MutationResponse } from "@/models";
import {
  SASS,
  SASS_TEMPLATE,
  sclrMutationFunctions,
  sassMutationFunctions,
} from "@/models/stff";
import {
  CLRM,
  CLRM_TEMPLATE,
  mutationFunctions as clrmMutationFunctions,
} from "@/models/clrm";
import Popup from "@/components/shared/Popup";
import { focusFirstInvalidField } from "@/utils/forms";
import SCLRAddEditForm from "@/components/records/staff/SCLRAddEditForm";
import { SASSAddEditForm } from "@/components/records/staff/SASSAddEditForm";
import { CLRMAddEditForm } from "@/components/records/classroom/CLRMAddEditForm";
import { RECORD_TYPE_NAMES } from "./hooks/useDiscrepancyPopup";
import { useLeaData } from "@/queries";
import { useState } from "react";

type CreateRecordPopupProps = {
  recordType: string;
  defaults: Record<string, unknown>;
  onClose: () => void;
  onBack: () => void;
};

export function CreateRecordPopup({
  recordType,
  defaults,
  onClose,
  onBack,
}: CreateRecordPopupProps) {
  const formId = `frm${recordType}Create`;
  const recordLabel = RECORD_TYPE_NAMES[recordType] || recordType;

  const leaContext = useRouteContext({ from: "/$leaid" });
  const { data: leaData } = useLeaData(leaContext.currentUserOrg.esiIdPath);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [sclrResult, setSclrResult] = useState<MutationResponse<SCLR> | null>(
    null
  );
  const [sassResult, setSassResult] = useState<MutationResponse<SASS> | null>(
    null
  );
  const [clrmResult, setClrmResult] = useState<MutationResponse<CLRM> | null>(
    null
  );

  const sclrForm = useForm<SCLR>({
    defaultValues: {
      ...SCLR_TEMPLATE,
      PreschoolCode: (defaults.PreschoolCode as string) || "",
      ProgramYear: (defaults.ProgramYear as string) || "",
      LocalStaffId: (defaults.LocalStaffId as string) || "",
      LocalClassroomId: (defaults.LocalClassroomId as string) || "",
    },
  });

  const sassForm = useForm<SASS>({
    defaultValues: {
      ...SASS_TEMPLATE,
      PreschoolCode: (defaults.PreschoolCode as string) || "",
      ProgramYear: (defaults.ProgramYear as string) || "",
      LocalStaffId: (defaults.LocalStaffId as string) || "",
    },
  });

  const clrmForm = useForm<CLRM>({
    defaultValues: {
      ...CLRM_TEMPLATE,
      PreschoolCode: (defaults.PreschoolCode as string) || "",
      ProgramYear: (defaults.ProgramYear as string) || "",
      LocalClassroomId: (defaults.LocalClassroomId as string) || "",
    },
  });

  if (!leaData) {
    return (
      <Popup
        show={true}
        header={`Create ${recordLabel} Record`}
        size="lg"
        onClose={onClose}
      >
        <div>Loading...</div>
      </Popup>
    );
  }

  const sclrMutateFns = sclrMutationFunctions(leaData, setSclrResult);
  const sassMutateFns = sassMutationFunctions(leaData, setSassResult);
  const clrmMutateFns = clrmMutationFunctions(leaData, setClrmResult);

  const onSubmitSCLR: SubmitHandler<SCLR> = async (data) => {
    setIsSubmitting(true);
    setSaveError(null);
    try {
      await sclrMutateFns.handleSave(data);
      onClose();
    } catch (error) {
      console.error("Error saving SCLR:", error);
      setSaveError("Failed to save SCLR record. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitSASS: SubmitHandler<SASS> = async (data) => {
    setIsSubmitting(true);
    setSaveError(null);
    try {
      await sassMutateFns.handleSave(data);
      onClose();
    } catch (error) {
      console.error("Error saving SASS:", error);
      setSaveError("Failed to save SASS record. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitCLRM: SubmitHandler<CLRM> = async (data) => {
    setIsSubmitting(true);
    setSaveError(null);
    try {
      await clrmMutateFns.handleSave(data);
      onClose();
    } catch (error) {
      console.error("Error saving CLRM:", error);
      setSaveError("Failed to save CLRM record. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderForm = () => {
    switch (recordType) {
      case "SCLR":
        return (
          <Form id={formId} onSubmit={sclrForm.handleSubmit(onSubmitSCLR)}>
            <SCLRAddEditForm
              staffClassroom={undefined}
              form={sclrForm}
              sclrDefaults={{
                PreschoolCode: (defaults.PreschoolCode as string) || "",
                ProgramYear: (defaults.ProgramYear as string) || "",
                LocalStaffId: (defaults.LocalStaffId as string) || "",
                LocalClassroomId: (defaults.LocalClassroomId as string) || "",
              }}
            />
          </Form>
        );
      case "SASS":
        return (
          <Form id={formId} onSubmit={sassForm.handleSubmit(onSubmitSASS)}>
            <SASSAddEditForm
              selectedStaffAssignment={undefined}
              form={sassForm}
              sassDefaults={{
                PreschoolCode: (defaults.PreschoolCode as string) || "",
                ProgramYear: (defaults.ProgramYear as string) || "",
                LocalStaffId: (defaults.LocalStaffId as string) || "",
              }}
            />
          </Form>
        );
      case "CLRM":
        return (
          <Form
            id={formId}
            onSubmit={clrmForm.handleSubmit(onSubmitCLRM, () =>
              focusFirstInvalidField(`#${formId}`)
            )}
          >
            <CLRMAddEditForm selectedClassroom={undefined} form={clrmForm} />
          </Form>
        );
      default:
        return (
          <div className="alert alert-warning">
            Form for record type "{recordType}" is not yet implemented.
          </div>
        );
    }
  };

  return (
    <Popup
      show={true}
      header={`Create ${recordLabel} Record`}
      size="lg"
      onClose={onClose}
      hasButton={false}
      submitButtonText={
        isSubmitting ? "Saving..." : `Save ${recordLabel} Record`
      }
      submitFormId={formId}
      secondButtonText="Back"
      onSecondButtonClick={onBack}
    >
      <div className="alert alert-info mb-3">
        <strong>Creating missing record:</strong> This {recordLabel} record is
        required to resolve the data discrepancy.
      </div>

      {saveError && <div className="alert alert-danger mb-3">{saveError}</div>}

      {renderForm()}
    </Popup>
  );
}
